import React, { Suspense, lazy } from 'react';
import 'antd/dist/reset.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import CookieConsent from './components/CookieConsent';
import Login from './Auth/Login';
import LandingPage from './LandingPage';
import About from './About';
import Signup from './Signup';
import Payment from './Payment';
import PaymentSuccess from './PaymentSuccess';
import PaymentFailed from './PaymentFailed';
import EmailVerification from './Auth/EmailVerification';
import OAuthCallback from './Auth/OAuthCallback';
import ResetPassword from './Auth/ResetPassword';
import ConfirmReset from './Auth/ConfirmReset';
import SALayout from './Super Admin/SALayout';
import SADashboard from './Super Admin/Dashboard';
import AdminBODashboard from './Admin Business owner/Dashboard';
import WhiteLabelDashboard from './White Label/Dashboard';
import WLClients from './White Label/Clients';
import WLBranding from './White Label/Branding';
// Super Admin pages
import AllUsers from './Super Admin/Users Management/AllUsers';
import ApproveSuspend from './Super Admin/Users Management/ApproveSuspend';
import DeleteAccount from './Super Admin/Users Management/DeleteAccount';
import WebsitesManagement from './Super Admin/Websites Management/WebsitesManagement';
import SnippetsManagement from './Super Admin/Snippets Management/SnippetsManagement';
import RevenueEarnings from './Super Admin/Revenue Management/RevenueEarnings';
import LogsAndActions from './Super Admin/LogsAndActions';
import PlatformSettingsPage from './Super Admin/Platform Settings/PlatformSettingsPage';
import BillingPlans from './Super Admin/Platform Settings/BillingPlans';
import AIPipeline from './Super Admin/AI Pipeline/AIPipeline';
import SuperAdminAPIPortal from './Super Admin/API Portal/APIPortal';
import VLayout from './Viewer/VLayout';
import ViewerDashboard from './components/dashboards/ViewerDashboard';
import ViewerWebsites from './Viewer/Websites';
import ViewerSnippets from './Viewer/Snippets';
import ViewerAnalytics from './Viewer/Analytics';
import Support from './Support/Support';
import AdminLayout from './Admin Business owner/AdminLayout';
// Admin Business owner pages
import WebsitesSnippetsPage from './Admin Business owner/WebsitesSnippets/WebsitesSnippetsPage';
import AnalyticsReportsPage from './Admin Business owner/Analytics/AnalyticsReportsPage';
import FormAnalytics from './Admin Business owner/FormAnalytics';
import SessionRecordings from './Admin Business owner/SessionRecordings';
import AccountSettingsPage from './Admin Business owner/Settings/AccountSettingsPage';
import ManageTeamAccess from './Admin Business owner/Team & Roles/ManageTeamAccess';
import APIPortal from './Admin Business owner/API Portal/APIPortal';
import APIKeys from './Admin Business owner/Account Settings/APIKeys';
import UserProfile from './Profile/UserProfile';
import HeatMap from './Admin Business owner/HeatMap/HeatMap';
import ChannelComparison from './Admin Business owner/ChannelComparison';

// Suppress ResizeObserver loop errors (common in React apps with dynamic layouts)
const suppressResizeObserverError = () => {
  // Suppress console errors
  const originalError = window.console.error;
  window.console.error = (...args) => {
    if (typeof args[0] === 'string' && args[0].includes('ResizeObserver loop completed with undelivered notifications')) {
      return;
    }
    originalError.apply(window.console, args);
  };

  // Suppress runtime errors
  window.addEventListener('error', (e) => {
    if (e.message && e.message.includes('ResizeObserver loop completed with undelivered notifications')) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });

  // Suppress unhandled promise rejections
  window.addEventListener('unhandledrejection', (e) => {
    if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver loop completed with undelivered notifications')) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });
};

suppressResizeObserverError();

const AIChat = lazy(() => import('./Admin Business owner/AI Chat/AIChat'));

function RequireAuth({ children }) {
  const user = localStorage.getItem('user');
  const token = localStorage.getItem('token');
  if (!user || !token) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/payment" element={<Payment />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/failed" element={<PaymentFailed />} />
        <Route path="/auth/login" element={<Login />} />
        <Route path="/auth/verify-email" element={<EmailVerification />} />
        <Route path="/auth/reset" element={<ResetPassword />} />
        <Route path="/auth/reset-confirm" element={<ConfirmReset />} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/dashboard/super-admin" element={<RequireAuth><SALayout /></RequireAuth>}>
          <Route index element={<SADashboard />} />
          {/* Account */}
          <Route path="profile" element={<UserProfile />} />
          {/* Users Management */}
          <Route path="users" element={<AllUsers />} />
          <Route path="users/approve" element={<ApproveSuspend />} />
          <Route path="users/delete" element={<DeleteAccount />} />
          {/* Websites Management */}
          <Route path="websites" element={<WebsitesManagement />} />
          {/* Snippets Management */}
          <Route path="snippets" element={<SnippetsManagement />} />
          {/* Revenue & Earnings */}
          <Route path="revenue" element={<RevenueEarnings />} />
          {/* Logs & Actions */}
          <Route path="logs" element={<LogsAndActions />} />
          {/* Platform Settings */}
          <Route path="settings" element={<PlatformSettingsPage />} />
          <Route path="settings/billing" element={<BillingPlans />} />
          {/* AI Pipeline */}
          <Route path="ai-pipeline" element={<AIPipeline />} />
          {/* API Portal */}
          <Route path="api-portal" element={<SuperAdminAPIPortal />} />
          {/* White Label */}
          <Route path="white-label" element={<WhiteLabelDashboard />} />
          <Route path="white-label/clients" element={<WLClients />} />
          <Route path="white-label/branding" element={<WLBranding />} />
          <Route path="white-label/support" element={<Support />} />
          {/* Support */}
          <Route path="support" element={<Support />} />
        </Route>
        {/* Admin nested routes with sidebar layout */}
        <Route path="/dashboard/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>
          <Route index element={<AdminBODashboard />} />
          {/* Websites & Snippets */}
          <Route path="websites-snippets" element={<WebsitesSnippetsPage />} />
          {/* Analytics & Reports */}
          <Route path="analytics" element={<AnalyticsReportsPage />} />
          {/* Channel Comparison */}
          <Route path="channel-comparison" element={<ChannelComparison />} />
          {/* AI Chat */}
          <Route
            path="ai-chat"
            element={(
              <Suspense fallback={<div>Loading AI Chat…</div>}>
                <AIChat />
              </Suspense>
            )}
          />
          {/* HeatMap */}
          <Route path="heatmap" element={<HeatMap />} />
          {/* Form Analytics */}
          <Route path="form-analytics" element={<FormAnalytics />} />
          {/* Session Recordings */}
          <Route path="session-recordings" element={<SessionRecordings />} />
          {/* Team & Roles */}
          <Route path="team/manage" element={<ManageTeamAccess />} />
          {/* API Portal */}
          <Route path="api-portal" element={<APIPortal />} />
          {/* Account Settings */}
          <Route path="settings" element={<AccountSettingsPage />} />
          <Route path="settings/api-keys" element={<APIKeys />} />
          {/* Support */}
          <Route path="support" element={<Support />} />
        </Route>
        <Route path="/dashboard/viewer" element={<RequireAuth><VLayout /></RequireAuth>}>
          <Route index element={<ViewerDashboard />} />
          <Route path="websites" element={<ViewerWebsites />} />
          <Route path="snippets" element={<ViewerSnippets />} />
          <Route path="analytics" element={<ViewerAnalytics />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="support" element={<Support />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CookieConsent />
    </BrowserRouter>
  );
}

export default App;
